import { useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface InteractiveStatusSwitchProps {
    initialStatus: any;
    onToggle: (newStatus: any) => Promise<void>;
    activeLabel?: string;
    inactiveLabel?: string;
    activeValue?: any;
    inactiveValue?: any;
    entityName?: string;
    /** Không có quyền sửa: chỉ hiện trạng thái, không bấm đổi được */
    disabled?: boolean;
}

export function InteractiveStatusSwitch({
    initialStatus,
    onToggle,
    activeLabel = 'Hoạt động',
    inactiveLabel = 'Ngưng',
    activeValue = 'ACTIVE',
    inactiveValue = 'INACTIVE',
    entityName = 'đối tượng này',
    disabled = false,
}: InteractiveStatusSwitchProps) {
    const [status, setStatus] = useState(initialStatus);
    const [isLoading, setIsLoading] = useState(false);
    
    // Dialog state
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [pendingChecked, setPendingChecked] = useState<boolean>(false);

    const isActive = status === activeValue;

    // Triggered when user clicks the switch/label
    const handleInitialToggle = (checked: boolean) => {
        if (isLoading || disabled) return;
        setPendingChecked(checked);
        setIsDialogOpen(true);
    };

    // Triggered when user clicks OK in dialog
    const handleConfirmToggle = async () => {
        setIsDialogOpen(false);
        const newStatus = pendingChecked ? activeValue : inactiveValue;
        
        // Optimistic UI update
        const previousStatus = status;
        setStatus(newStatus);
        setIsLoading(true);

        try {
            await onToggle(newStatus);
            
        } catch (error: any) {
            // Revert on error
            setStatus(previousStatus);
            toast.error(error?.message || 'Cập nhật trạng thái thất bại');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="flex items-center space-x-2">
                <Switch 
                    checked={isActive} 
                    onCheckedChange={handleInitialToggle} 
                    disabled={isLoading || disabled} 
                    className="data-[state=checked]:bg-emerald-500 scale-90"
                />
                <Label className={`text-xs font-medium cursor-pointer ${isActive ? 'text-emerald-700' : 'text-slate-500'}`} onClick={() => !isLoading && !disabled && handleInitialToggle(!isActive)}>
                    {isActive ? activeLabel : inactiveLabel}
                </Label>
            </div>

            <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Xác nhận thay đổi trạng thái</AlertDialogTitle>
                        <AlertDialogDescription>
                            Bạn có chắc chắn muốn chuyển trạng thái của {entityName} thành <strong className="text-slate-900">{pendingChecked ? activeLabel : inactiveLabel}</strong> không?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isLoading}>Huỷ</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmToggle} disabled={isLoading} className={pendingChecked ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}>
                            Xác nhận
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
